import { Helmet } from 'react-helmet-async';
import { LoadingButton } from '@mui/lab';
import { useState, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Box, CircularProgress, TextField, Typography, Grid, Divider } from '@mui/material';
import axiosInstance from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';

export default function SiteConfigForm() { 
  const [siteConfig, setSiteConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get("/siteConfig/siteConfig")
      .then(res => setSiteConfig(res.data ?? {}))
      .catch(() => enqueueSnackbar('Site config fetch failed', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const onSubmit = () => {
    setSubmitting(true);
    axiosInstance.post("/siteConfig/siteConfigForm", objectToFormData(siteConfig))
      .then(res => {
        setSiteConfig(res.data ?? {});
        enqueueSnackbar('Site config updated', { variant: 'success' })
      })
      .catch(() => enqueueSnackbar('Site config fetch failed', { variant: 'error' }))
      .finally(() => setSubmitting(false));
  }

  const validateEmail = (email) => {
    if (!email) return "Email is required";
    const regex = /\S+@\S+\.\S+/;
    if (!regex.test(email)) return "Invalid email";
    if (!email.toLowerCase().endsWith('@nhidirect.com')) return "Email must end with: '@nhidirect.com'";
    return null;
  }

  const onHoldEmailError = validateEmail(siteConfig.onHoldEmail);
  const orderEmailError = validateEmail(siteConfig.orderEmail);
  const quoteEmailError = validateEmail(siteConfig.quoteEmail);
  const infoEmailError = validateEmail(siteConfig.infoEmail);

  return (<>
    <Helmet>
      <title>Site Config | NHIDirect</title>
    </Helmet>

    <Box sx={{ height: "calc(100% - 55px)", mt: 2 }}>
      {!loading ? <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography variant="h5">Address</Typography>
          <Typography variant="subtitle1">Street</Typography>
          <TextField
            name="streetAddress"
            size="small"
            placeholder="Enter Street Address"
            fullWidth
            value={siteConfig.streetAddress ?? ""}
            onChange={(e) => setSiteConfig(config => ({ ...config, streetAddress: e.target.value }))}
          />
        </Grid>
        <Grid container spacing={2} item xs={12}>
          <Grid item xs={4}>
            <Typography variant="subtitle1">City</Typography>
            <TextField
              name="city"
              size="small"
              placeholder="Enter City Address"
              fullWidth
              value={siteConfig.city ?? ""}
              onChange={(e) => setSiteConfig(config => ({ ...config, city: e.target.value }))}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography variant="subtitle1">State</Typography>
            <TextField
              name="state"
              size="small"
              placeholder="Enter State Address"
              fullWidth
              value={siteConfig.state ?? ""}
              onChange={(e) => setSiteConfig(config => ({ ...config, state: e.target.value }))}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography variant="subtitle1">Zip</Typography>
            <TextField
              name="zip"
              size="small"
              placeholder="Enter Zip Address"
              fullWidth
              value={siteConfig.zip ?? ""}
              onChange={(e) => setSiteConfig(config => ({ ...config, zip: e.target.value }))}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Phone Number</Typography>
          <TextField
            name="phoneNumber"
            size="small"
            placeholder="Enter Phone Number"
            fullWidth
            value={siteConfig.phoneNumber ?? ""}
            onChange={(e) => setSiteConfig(config => ({ ...config, phoneNumber: e.target.value }))}
          />
        </Grid>
        <Grid sx={{ mt: 2, mb: 1}} item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h5">Emails</Typography>
          <Typography variant="subtitle1">On Hold Email*</Typography>
          <TextField
            name="onHoldEmail"
            size="small"
            placeholder="Enter On Hold Email"
            fullWidth
            value={siteConfig.onHoldEmail ?? ""}
            FormHelperTextProps={{ component: "div" }}
            helperText={onHoldEmailError || <Box>
              <Box>This email gets sent out when the facility places an order but was put on hold.</Box>
              <Box>It is sent to the person who placed the order and to any facility contacts marked for ordering.</Box>
            </Box>}
            error={onHoldEmailError != null}
            onChange={(e) => setSiteConfig(config => ({ ...config, onHoldEmail: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Order Email*</Typography>
          <TextField
            name="orderEmail"
            size="small"
            placeholder="Enter Order Email"
            fullWidth
            value={siteConfig.orderEmail ?? ""}
            FormHelperTextProps={{ component: "div" }}
            helperText={orderEmailError || <Box>
              <Box>This email gets sent out when the facility places an order.</Box>
              <Box>It is sent to the person who placed the order and to any facility contacts marked for ordering.</Box>
              <Box>An additional email is send to each vendor with thier respective items.</Box>
            </Box>}
            error={orderEmailError != null}
            onChange={(e) => setSiteConfig(config => ({ ...config, orderEmail: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Request Quote Email*</Typography>
          <TextField
            name="requestQuoteEmail"
            size="small"
            placeholder="Enter Request Quote Email"
            fullWidth
            value={siteConfig.quoteEmail ?? ""}
            FormHelperTextProps={{ component: "div" }}
            helperText={quoteEmailError || <Box>
              <Box>This email gets sent out when the facility request a quote for an item.</Box>
              <Box>It only sends to itself it will not include the person who requested the quote.</Box>
            </Box>}
            error={quoteEmailError != null}
            onChange={(e) => setSiteConfig(config => ({ ...config, quoteEmail: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Info Email*</Typography>
          <TextField
            name="infoEmail"
            size="small"
            placeholder="Enter Info Email"
            fullWidth
            value={siteConfig.infoEmail ?? ""}
            FormHelperTextProps={{ component: "div" }}
            helperText={infoEmailError || <Box>This email gets sent out for any email result (e.g. resend invoices, and bulk upload results).</Box>}
            error={infoEmailError != null}
            onChange={(e) => setSiteConfig(config => ({ ...config, infoEmail: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12}>
          <LoadingButton loading={submitting} sx={{ display: "flex", justifySelf: "center", mb: 2, px: 7 }}
            color="purple" size="medium" variant="contained" onClick={onSubmit}>
            Save
          </LoadingButton>
        </Grid>
      </Grid> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
    </Box>
  </>);
}
